import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Badge } from "reactstrap";
import { Link } from "react-router-dom";

//i18n
import { withTranslation } from "react-i18next";
import WelcomeComp from "../shared/WelcomeComp";
import UserService from "Api/Services/UserService";
import { Table } from "reactstrap";
import PageTitle from "../../components/Common/PageTitle";
import DoctorSelector from "../../components/Common/DoctorSelector";
import {
  getStoredSelectedDoctorId,
  isGroupLoginUser,
  setStoredSelectedDoctorId,
} from "../../helpers/doctorSelection";

const parsePortalDate = (dateValue) => {
  const dateMatch = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return dateMatch
    ? new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]))
    : new Date(dateValue);
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const UserDashboard = props => {
  document.title = "Dashboard | Infuse One";
  const [dashboardData, setDashboardData] = useState({
    summaryMetrics: {
      totalPatients: null,
      activePatients: null,
      completedTreatments: null,
      upcomingAppointments: null,
      totalDrugsAssigned: null
    },
    patientStatus: {
      awaitingVerification: null,
      receivingTreatment: null,
      completedTreatment: null,
      missedAppointments: null,
      upcomingAppointments: null
    },
    recentAppointments: []
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState(getStoredSelectedDoctorId());
  const [groupLogin] = useState(isGroupLoginUser());

  const loadAccessibleDoctors = async () => {
    try {
      const response = await UserService.GetAccessibleDoctors().json();

      if (!response.status) {
        setError(response.message || "Something went wrong");
        return;
      }

      const availableDoctors = response.data || [];
      const fallbackDoctorId = response.selected_doctor?.id ? String(response.selected_doctor.id) : "";
      const storedDoctorId = getStoredSelectedDoctorId();
      const nextDoctorId = availableDoctors.some(doctor => String(doctor.id) === storedDoctorId)
        ? storedDoctorId
        : fallbackDoctorId;

      setDoctors(availableDoctors);
      setSelectedDoctorId(nextDoctorId);
      setStoredSelectedDoctorId(nextDoctorId);
      return nextDoctorId;
    } catch (error) {
      setError("Something went wrong");
      return "";
    }
  };

  const fetchDashboardData = async doctorId => {
    try {
      const today = new Date();
      const todayStr = toDateInputValue(today);
      const plus14 = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
      const plus14Str = toDateInputValue(plus14);
      
      const response = await UserService.getDoctorDashboardData(doctorId);
      const upcomingResponse = await UserService.getAppointments({
        page: 1,
        perPage: 1,
        sort_field: 'appt_date',
        sort_direction: 'asc',
        start_date: todayStr,
        end_date: plus14Str,
        doctorId
      });
      
      let upcomingCount = null;
      if (upcomingResponse.status && upcomingResponse.data && upcomingResponse.data.pagination) {
        upcomingCount = upcomingResponse.data.pagination.total;
      }
      
      if (response?.status && response?.data?.success) {
        const dashboard = response.data.data;
        const newData = {
          summaryMetrics: {
            totalPatients: dashboard.total_patients,
            totalDrugsAssigned: dashboard.total_infuse_one_appointments,
            activePatients: dashboard.patients_in_treatment,
            completedTreatments: dashboard.patients_treated,
            upcomingAppointments: dashboard.upcoming_appointments
          },
          patientStatus: {
            awaitingVerification: dashboard.patients_in_verification,
            receivingTreatment: dashboard.patients_in_treatment,
            completedTreatment: dashboard.patients_treated,
            missedAppointments: 0,
            upcomingAppointments: upcomingCount
          },
          recentAppointments: []
        };
        setDashboardData(newData);
      }
    } catch (error) {
      setError('Failed to fetch dashboard data');
    }
  };

  const fetchRecentAppointments = async doctorId => {
    setLoadingAppointments(true);
    try {
      const today = new Date();
      const todayStr = toDateInputValue(today);
      const plus14 = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
      const plus14Str = toDateInputValue(plus14);
      
      const response = await UserService.getAppointments({
        page: 1,
        perPage: 10,
        sort_field: 'appt_date',
        sort_direction: 'asc',
        start_date: todayStr,
        end_date: plus14Str,
        doctorId
      });
      
      if (response.status && response.data && response.data.appointments) {
        const formattedAppointments = response.data.appointments.map(appointment => ({
          id: appointment.id,
          appointmentDate: appointment.appt_date,
          patientName: appointment.patient_name,
          location: appointment.location_name,
          icdDescription: appointment.icd_description,
          drug: appointment.prescription || 'Not specified',
          status: appointment.appt_status ? appointment.appt_status.toLowerCase() : 'unknown'
        }));
        
        setDashboardData(prevData => ({
          ...prevData,
          recentAppointments: formattedAppointments
        }));
      }
    } catch (error) {
      console.error('Error fetching recent appointments');
    } finally {
      setLoadingAppointments(false);
    }
  };

  const loadDashboard = async doctorId => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchDashboardData(doctorId),
        fetchRecentAppointments(doctorId),
      ]);
    } catch (error) {
      setError("Error loading dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (groupLogin) {
          const nextDoctorId = await loadAccessibleDoctors();
          if (!nextDoctorId) {
            setLoading(false);
          }
          return;
        }

        await loadDashboard();
      } catch (error) {
        console.error('Error in initial data fetch');
        setError('Error loading dashboard data');
      }
    };

    fetchData();
  }, [groupLogin]);

  useEffect(() => {
    if (!groupLogin || !selectedDoctorId) {
      return;
    }

    setStoredSelectedDoctorId(selectedDoctorId);
    loadDashboard(selectedDoctorId);
  }, [selectedDoctorId, groupLogin]);

  const summaryMetrics = [
    { 
      titleLine1: "Total Infuse One", 
      titleLine2: "Patients", 
      iconClass: "bx bx-user font-size-18", 
      value: dashboardData.summaryMetrics.totalPatients,
      url: "/patients",
      color: "primary"
    },
    { 
      titleLine1: "Total Infuse One", 
      titleLine2: "Appointments", 
      iconClass: "bx bx-capsule font-size-18", 
      value: dashboardData.summaryMetrics.totalDrugsAssigned,
      url: "/appointments",
      color: "success"
    }
  ];

  const patientStatus = [
    { 
      titleLine1: "Patients Pending", 
      titleLine2: "Verification", 
      iconClass: "bx bx-user-check font-size-18", 
      value: dashboardData.patientStatus.awaitingVerification,
      url: "/patients?status=Verification",
      color: "warning"
    },
    { 
      titleLine1: "Patients In", 
      titleLine2: "Treatment", 
      iconClass: "bx bx-plus-medical font-size-18", 
      value: dashboardData.patientStatus.receivingTreatment,
      url: "/patients?status=Active",
      color: "info"
    },
    { 
      titleLine1: "Patients Completed", 
      titleLine2: "Treatment", 
      iconClass: "bx bx-check-circle font-size-18", 
      value: dashboardData.patientStatus.completedTreatment,
      url: "/patients?status=Complete",
      color: "success"
    },
    { 
      titleLine1: "Upcoming Appointments", 
      titleLine2: "(Next 14 Days)", 
      iconClass: "bx bx-calendar-event font-size-18", 
      value: dashboardData.patientStatus.upcomingAppointments,
      url: "/appointments",
      color: "primary"
    }
  ];

  const handlePatientMetricClick = (status) => {
    window.location.href = `/patients?status=${status}`;
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="container-fluid" style={{ padding: '24px 10px' }}>
            {/* Welcome Card and Summary Metrics */}
            <Row>
              <Col xl="6">
                <Card className="shadow-sm h-100">
                  <CardBody className="p-0">
                    <div className="p-4">
                      <div className="skeleton-loader" style={{ height: '24px', width: '60%', marginBottom: '16px' }}></div>
                      <div className="skeleton-loader" style={{ height: '16px', width: '80%' }}></div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col xl="6">
                <Row>
                  <Col md="6" className="mb-4">
                    <Card className="mini-stats-wid">
                      <CardBody className="p-4">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <div className="skeleton-loader" style={{ height: '16px', width: '70%', marginBottom: '8px' }}></div>
                            <div className="skeleton-loader" style={{ height: '32px', width: '40%' }}></div>
                          </div>
                          <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                            <span className="avatar-title rounded-circle bg-primary">
                              <i className="bx bx-user font-size-24"></i>
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="6" className="mb-4">
                    <Card className="mini-stats-wid">
                      <CardBody className="p-4">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <div className="skeleton-loader" style={{ height: '16px', width: '70%', marginBottom: '8px' }}></div>
                            <div className="skeleton-loader" style={{ height: '32px', width: '40%' }}></div>
                          </div>
                          <div className="avatar-sm rounded-circle bg-success align-self-center mini-stat-icon">
                            <span className="avatar-title rounded-circle bg-success">
                              <i className="bx bx-capsule font-size-24"></i>
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Patient Status Breakdown */}
            <Row>
              {[1, 2, 3, 4].map((index) => (
                <Col md="3" key={index} className="mb-4">
                  <Card className="mini-stats-wid">
                    <CardBody className="p-4">
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <div className="skeleton-loader" style={{ height: '16px', width: '70%', marginBottom: '8px' }}></div>
                          <div className="skeleton-loader" style={{ height: '32px', width: '40%' }}></div>
                        </div>
                        <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                          <span className="avatar-title rounded-circle bg-primary">
                            <i className="bx bx-user font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Recent Appointments */}
            <Row>
              <Col>
                <Card>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="card-title mb-0">Recent Appointments</h4>
                    </div>
                    <div className="table-responsive">
                      <Table className="table-centered table-nowrap mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Patient</th>
                            <th>Location</th>
                            <th>Drug</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[1, 2, 3].map((index) => (
                            <tr key={index}>
                              <td><div className="skeleton-loader" style={{ height: '16px', width: '80%' }}></div></td>
                              <td><div className="skeleton-loader" style={{ height: '16px', width: '60%' }}></div></td>
                              <td><div className="skeleton-loader" style={{ height: '16px', width: '70%' }}></div></td>
                              <td><div className="skeleton-loader" style={{ height: '16px', width: '50%' }}></div></td>
                              <td><div className="skeleton-loader" style={{ height: '16px', width: '40%' }}></div></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="container-fluid user-page-shell">
            <PageTitle
              title="Dashboard"
              actions={groupLogin ? (
                <DoctorSelector
                  doctors={doctors}
                  selectedDoctorId={selectedDoctorId}
                  onChange={setSelectedDoctorId}
                  compact
                />
              ) : null}
            />
            <Row>
              <Col xl="6">
                <Card className="shadow-sm h-100">
                  <CardBody className="p-0">
                    <WelcomeComp doctorId={groupLogin ? selectedDoctorId : ""} />
                  </CardBody>
                </Card>
              </Col>
              <Col xl="6">
                <Row>
                  <Col md="6" className="mb-4">
                    <Card className="mini-stats-wid">
                      <CardBody className="p-4">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium kpi-title">{summaryMetrics[0].titleLine1}<br />{summaryMetrics[0].titleLine2}</p>
                            <h2 className="mb-0 display-6 fw-semibold">
                              {dashboardData.summaryMetrics.totalPatients === null ? (
                                <div className="skeleton-loader" style={{ height: '32px', width: '40%' }}></div>
                              ) : (
                                dashboardData.summaryMetrics.totalPatients
                              )}
                            </h2>
                          </div>
                          <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                            <span className="avatar-title rounded-circle bg-primary">
                              <i className="bx bx-user font-size-24"></i>
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 border-top pt-2">
                          <Link to="/patients" className="text-decoration-none">
                            View All Patients
                            <i className="mdi mdi-arrow-right ms-1"></i>
                          </Link>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="6" className="mb-4">
                    <Card className="mini-stats-wid">
                      <CardBody className="p-4">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium kpi-title">{summaryMetrics[1].titleLine1}<br />{summaryMetrics[1].titleLine2}</p>
                            <h2 className="mb-0 display-6 fw-semibold">
                              {dashboardData.summaryMetrics.totalDrugsAssigned === null ? (
                                <div className="skeleton-loader" style={{ height: '32px', width: '40%' }}></div>
                              ) : (
                                dashboardData.summaryMetrics.totalDrugsAssigned
                              )}
                            </h2>
                          </div>
                          <div className="avatar-sm rounded-circle bg-success align-self-center mini-stat-icon">
                            <span className="avatar-title rounded-circle bg-success">
                              <i className="bx bx-capsule font-size-24"></i>
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 border-top pt-2">
                          <Link to="/appointments" className="text-decoration-none">
                            View All Appointments
                            <i className="mdi mdi-arrow-right ms-1"></i>
                          </Link>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Patient Status Breakdown */}
            <Row className="mt-5">
              <Col xl="12">
                <h4 className="card-title mb-4">Patient Status Breakdown</h4>
              </Col>
              {patientStatus.map((status, index) => (
                <Col key={index} xl="3" md="6">
                  <Card className="mini-stats-wid">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <p className="text-muted fw-medium kpi-title">{status.titleLine1}<br />{status.titleLine2}</p>
                          <h4 className="mb-0 kpi-status-number">
                            {dashboardData.patientStatus[Object.keys(dashboardData.patientStatus)[index]] === null ? (
                              <div className="skeleton-loader" style={{ height: '32px', width: '40%' }}></div>
                            ) : (
                              status.value
                            )}
                          </h4>
                        </div>
                        <div className={`avatar-sm rounded-circle bg-${status.color} align-self-center mini-stat-icon`}>
                          <span className={`avatar-title rounded-circle bg-${status.color}`}>
                            <i className={`bx ${status.iconClass} font-size-24`}></i>
                          </span>
                        </div>
                      </div>
                      <div className="mt-3">
                        <Link to={status.url} className="text-decoration-none">
                          {index === 3 ? 'View Upcoming Appointments' : `View ${status.titleLine1} ${status.titleLine2}`}
                          <i className="mdi mdi-arrow-right ms-1"></i>
                        </Link>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Recent Appointments Table */}
            <Row className="mt-4">
              <Col xl="12">
                <Card>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="card-title mb-0">Upcoming Appointments (Next 14 Days)</h4>
                      <Link to="/appointments" className="btn btn-primary btn-sm">
                        View All Appointments
                      </Link>
                    </div>
                    <div className="table-responsive">
                      <Table className="table-centered table-nowrap mb-0">
                        <thead>
                          <tr>
                            <th>Appointment Date</th>
                            <th>Patient Name</th>
                            <th>Location</th>
                            <th>Prescription</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingAppointments ? (
                            <tr>
                              <td colSpan="5" className="text-center">
                                <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                Loading recent appointments...
                              </td>
                            </tr>
                          ) : dashboardData.recentAppointments.length > 0 ? (
                            dashboardData.recentAppointments.map((appointment, index) => (
                              <tr key={appointment.id || index} style={{ padding: '1rem 0' }}>
                                <td style={{ padding: '1rem 0.75rem' }}>{appointment.appointmentDate ? (() => {
                                  const date = parsePortalDate(appointment.appointmentDate);
                                  const weekday = date.toLocaleString(undefined, { weekday: 'short' });
                                  const month = date.toLocaleString(undefined, { month: 'long' });
                                  const day = date.getDate();
                                  const year = date.getFullYear();
                                  return `${weekday}. ${month} ${day}, ${year}`;
                                })() : ''}</td>
                                <td style={{ padding: '1rem 0.75rem' }}>{appointment.patientName}</td>
                                <td style={{ padding: '1rem 0.75rem' }}>{appointment.location}</td>
                                <td style={{ padding: '1rem 0.75rem' }}>{appointment.drug}</td>
                                <td style={{ padding: '1rem 0.75rem' }}>
                                  <Badge 
                                    color={
                                      appointment.status?.toLowerCase() === "active" ? undefined :
                                      appointment.status?.toLowerCase() === "complete" || appointment.status?.toLowerCase() === "completed" ? undefined :
                                      appointment.status?.toLowerCase() === "scheduled" ? "warning" :
                                      appointment.status?.toLowerCase() === "canceled" || appointment.status?.toLowerCase() === "cancelled" ? "danger" :
                                      "secondary"
                                    }
                                    className={`status-badge${appointment.status?.toLowerCase() === "active" ? ' status-badge-active' : ''}${(appointment.status?.toLowerCase() === "complete" || appointment.status?.toLowerCase() === "completed") ? ' status-badge-complete' : ''}`}
                                  >
                                    {appointment.status ? appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1).toLowerCase() : 'Unknown'}
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center" style={{ padding: '1rem 0.75rem' }}>
                                No recent appointments to display
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      <style>
        {`
          .two-line-title {
            white-space: pre-line;
            word-break: break-word;
            line-height: 1.2;
            min-height: 2.4em;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
          }
          .kpi-title {
            margin-bottom: 0.75rem;
          }
          .kpi-large-number {
            font-size: 2.2rem;
            font-weight: 700;
            line-height: 1.1;
          }
          .kpi-status-number {
            font-size: 1.8rem;
            font-weight: 500;
            line-height: 1.1;
          }
          .status-badge-active {
            background-color: #4BA3FA !important;
            color: #fff !important;
            border-color: #4BA3FA !important;
          }
          .status-badge-complete {
            background-color: #2ECC8B !important;
            color: #fff !important;
            border-color: #2ECC8B !important;
          }
        `}
      </style>
    </React.Fragment>
  );
};

// UserDashboard.propTypes = {
//   t: PropTypes.any,
//   chartsData: PropTypes.any,
//   onGetChartsData: PropTypes.func,
// };

export default withTranslation()(UserDashboard);
